import { useSearchParams } from "react-router-dom";
import { Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { MaskLines, Reveal } from "@/components/site/Motion";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import { useContact } from "@/hooks/useContact";
import { T } from "@/context/ContentContext";
import { useSeo } from "@/hooks/useSeo";

export default function Contact() {
  useSeo({
    title: "Contact Trippy Tastic | Travel Agency in Mayur Vihar, Delhi",
    description: "Visit or call Trippy Tastic, travel agents in Mayur Vihar, New Delhi 110091. Georgia e-visa help, tour packages and callbacks. Phone +91 72176 02754.",
    path: "/contact",
  });
  const CONTACT = useContact();
  const CARDS = [
    { icon: Phone, label: "Call", value: CONTACT.phoneDisplay, href: `tel:+91${CONTACT.phone}`, id: "contact-call" },
    { icon: MessageCircle, label: "WhatsApp", value: <T k="contact.whatsapp-text">Chat with us</T>, href: CONTACT.whatsapp, id: "contact-whatsapp" },
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, id: "contact-email" },
  ];
  const [params] = useSearchParams();
  const corporate = params.get("type") === "corporate";
  return (
    <main className="bg-paper pt-32 sm:pt-40 pb-24" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <p className="eyebrow text-terra mb-4">{corporate ? <T k="contact.corp.eyebrow">Corporate offsites</T> : <T k="contact.eyebrow">Contact</T>}</p>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-[-0.04em] leading-[0.92]">
          <MaskLines lines={corporate
            ? [<T k="contact.corp.title.0">Teams that</T>, <T k="contact.corp.title.1" className="font-serif italic font-normal">travel well.</T>]
            : [<T k="contact.title.0">Say hello.</T>, <T k="contact.title.1" className="font-serif italic font-normal">We reply fast.</T>]} />
        </h1>
        <div className="mt-16 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-4">
            {CARDS.map(({ icon: Icon, label, value, href, id }, i) => (
              <Reveal key={label} delay={i * 0.08}>
                <a href={href} target={label === "WhatsApp" ? "_blank" : undefined} rel="noreferrer" data-testid={id}
                  className="group flex items-center justify-between rounded-[24px] bg-cream px-6 py-5 hover:bg-ink hover:text-cream transition-colors duration-500">
                  <span><T k={`contact.card.${id}`} className="eyebrow block text-terra group-hover:text-gold">{label}</T><span className="font-display text-xl font-bold">{value}</span></span>
                  <Icon className="h-6 w-6" />
                </a>
              </Reveal>
            ))}
            <Reveal delay={0.25} className="rounded-[24px] border border-ink/10 px-6 py-5">
              <T k="contact.office" className="eyebrow block text-terra mb-2">Office</T>
              <p className="flex gap-3" data-testid="contact-address"><MapPin className="h-5 w-5 shrink-0 mt-0.5" />{CONTACT.address}</p>
            </Reveal>
            <Reveal delay={0.3} className="rounded-[24px] overflow-hidden aspect-[4/3]">
              <iframe title="Trippy Tastic office map" loading="lazy" className="h-full w-full grayscale-[40%]" data-testid="contact-map"
                src={`https://www.google.com/maps?q=${encodeURIComponent(CONTACT.address)}&output=embed`} />
            </Reveal>
          </div>
          <Reveal className="lg:col-span-7 rounded-[32px] bg-ink p-6 sm:p-10 h-fit">
            <p className="font-serif italic text-3xl text-cream mb-6">{corporate ? <T k="contact.corp.form-title">Tell us about your team.</T> : <T k="contact.form-title">Allow us to call you back.</T>}</p>
            <EnquiryForm dark idPrefix="contact" preset={corporate ? { trip_title: "Corporate offsite enquiry" } : {}} />
          </Reveal>
        </div>
      </div>
    </main>
  );
}
